import "./StripeNotice.scss";
import { Alert, Button } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { MiniAlertLabel } from "molecules/miniLabels/MiniLabels";
import { buildStripeNoticeStatus } from "./buildStripeNoticeStatus";
import { showStripeNoticeModal } from "./showStripeNoticeModal";
import { useStripeAccountStatus } from "api/clients/useGetStripeAccountStatus";
import type { StripeAccountStatusResult } from "api/responses";

/** Displays a notice to update the Stripe connected account information. */
export const StripeNotice = ({ hideLink }: { readonly hideLink?: boolean }) => {
    const { isPending, isError, data } = useStripeAccountStatus();

    if (isPending || isError) {
        return null;
    }

    return <StripeNoticeAlert data={data} hideLink={hideLink} />;
};

export const StripeNoticeAlert = ({
    data,
    hideLink,
}: {
    readonly data: StripeAccountStatusResult;
    readonly hideLink?: boolean;
}) => {
    const { status, dueDate, stripeErrorReasons } = buildStripeNoticeStatus(data);
    const statusMap = buildStatusMap(dueDate, hideLink);

    if (status === "success") {
        return null;
    }

    return (
        <Alert
            icon={statusMap[status].icon}
            className="vyne-stripe-notice"
            type={status}
            message={statusMap[status].message}
            description={
                <p>
                    {statusMap[status].description}{" "}
                    {!hideLink && (
                        <Button type="link" onClick={() => void showStripeNoticeModal(stripeErrorReasons)}>
                            View Required Fields & Update
                        </Button>
                    )}
                </p>
            }
            showIcon
        />
    );
};

export const StripeNoticeBadge = ({
    data,
    hideLink,
}: {
    readonly data: StripeAccountStatusResult;
    readonly hideLink?: boolean;
}) => {
    const { status, dueDate } = buildStripeNoticeStatus(data);
    const statusMap = buildStatusMap(dueDate, hideLink);

    if (status === "success") {
        return <MiniAlertLabel type="success" message="Active" rounded={false} icon={<CheckCircleOutlined />} />;
    }

    return (
        <MiniAlertLabel
            type={status}
            title={statusMap[status].description}
            message={statusMap[status].badgeDescription}
            icon={statusMap[status].icon}
            rounded={false}
        />
    );
};

const buildStatusMap = (dueDate: string, hideLink?: boolean) => {
    const endingCharacter = hideLink ? "." : ":";

    const dueDateText = dueDate
        ? ` Starting on ${dueDate} additional data will be needed to continue using Vyne Payments${endingCharacter}`
        : ` Additional data will be needed to continue using Vyne Payments${endingCharacter}`;

    return {
        info: {
            icon: <InfoCircleOutlined />,
            type: "info",
            message: "Action Required: Update Your Stripe Business Info",
            description: `Our payment processing partner, Stripe, is updating their verification process for transacting.${dueDateText}`,
            badgeDescription: `New Settings Requested`,
        },
        warning: {
            icon: <ExclamationCircleOutlined />,
            type: "warning",
            message: "Action Required: Update Your Stripe Business Info",
            description: `Our payment processing partner, Stripe, is updating their verification process for transacting.${dueDateText}`,
            badgeDescription: `Required Settings Due`,
        },
        error: {
            icon: <ExclamationCircleOutlined />,
            type: "error",
            message: "Action Required: Update Your Stripe Business Info",
            description: `Our payment processing partner, Stripe, is updating their verification process for transacting. Additional data is needed to continue using Vyne Payments${endingCharacter}`,
            badgeDescription: `Required Settings Needed To Continue Using Vyne Payments`,
        },
    };
};
